/**
 * Client accessors for the landed staff reservation surface (backend `StaffBookingController` +
 * `StaffBookingQueryController`, DOMAIN.md §C.7). Every call funnels through `@repo/api-client`'s
 * fetch mutator (bearer token, RFC 9457 `ApiError`, auto Idempotency-Key on writes) — the shared
 * ADR-0020 seam the 409 conflict handler extends — and is typed by the ORVAL-GENERATED model types
 * (regenerated by `just gen`; never hand-edited). Thin accessors (not the generated hooks) so every
 * query keys under the shell's `["venue", venueId, "bookings", …]` convention and realtime
 * invalidation (`booking.*`) reaches all four views through one wiring.
 */
import { customInstance } from "@repo/api-client";
import type {
  ApproveRequestBody,
  CalendarDensityResponse,
  DeclineRequestBody,
  PageResponseStaffBookingSummaryResponse,
  StaffBookingResponse,
  StaffCancelBookingRequest,
  StaffCreateBookingRequest,
  StaffEditBookingRequest,
} from "@repo/api-client";
import type { BookingFilter } from "./searchParams";

const base = (venueId: string) => `/api/v1/venues/${encodeURIComponent(venueId)}/bookings`;

/** Manager step-up header (BK-8) the cancel flow sets after minting a `CANCEL_CONFIRMED_BOOKING`. */
export const STEP_UP_HEADER = "X-Step-Up-Authorization";

function listQuery(filter: BookingFilter, cursor: string | undefined, limit: number): string {
  const sp = new URLSearchParams();
  if (filter.q) sp.set("q", filter.q);
  if (filter.phone) sp.set("phone", filter.phone);
  if (filter.email) sp.set("email", filter.email);
  if (filter.from) sp.set("from", filter.from);
  if (filter.to) sp.set("to", filter.to);
  for (const s of filter.status) sp.append("status", s);
  for (const c of filter.channel) sp.append("channel", c);
  if (filter.partyMin !== undefined) sp.set("partyMin", String(filter.partyMin));
  if (filter.partyMax !== undefined) sp.set("partyMax", String(filter.partyMax));
  if (filter.zoneId) sp.set("zoneId", filter.zoneId);
  if (cursor) sp.set("cursor", cursor);
  sp.set("limit", String(limit));
  return sp.toString();
}

export const bookingsApi = {
  /** Cursor page of bookings for a filter (ADR-0022 — `nextCursor` only, no offset). */
  list: (
    venueId: string,
    filter: BookingFilter,
    cursor: string | undefined,
    limit: number,
    signal?: AbortSignal,
  ) =>
    customInstance<PageResponseStaffBookingSummaryResponse>(
      `${base(venueId)}?${listQuery(filter, cursor, limit)}`,
      { method: "GET", signal },
    ),

  /** Per-(day,status) counts for the calendar densities. Bare array, day-range bounded. */
  calendar: (
    venueId: string,
    from: string,
    to: string,
    status: string[],
    channel: string[],
    signal?: AbortSignal,
  ) => {
    const sp = new URLSearchParams({ from, to });
    for (const s of status) sp.append("status", s);
    for (const c of channel) sp.append("channel", c);
    return customInstance<CalendarDensityResponse[]>(`${base(venueId)}/calendar?${sp.toString()}`, {
      method: "GET",
      signal,
    });
  },

  /** The PENDING request inbox (read-only here — approve/decline is request-to-book). */
  inbox: (venueId: string, cursor: string | undefined, limit: number, signal?: AbortSignal) => {
    const sp = new URLSearchParams({ limit: String(limit) });
    if (cursor) sp.set("cursor", cursor);
    return customInstance<PageResponseStaffBookingSummaryResponse>(
      `${base(venueId)}/inbox?${sp.toString()}`,
      { method: "GET", signal },
    );
  },

  /** Manual/phone/walk-in creation — guest match-or-create + blacklist gate run server-side (R5). */
  create: (venueId: string, body: StaffCreateBookingRequest) =>
    customInstance<StaffBookingResponse>(base(venueId), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),

  /** Field-mask edit (absent field == unchanged, BK-4); CAS on `version`; 409 → conflict modal. */
  edit: (venueId: string, bookingId: string, body: StaffEditBookingRequest) =>
    customInstance<StaffBookingResponse>(`${base(venueId)}/${encodeURIComponent(bookingId)}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),

  /** Cancel (CAS on `version`). A waiter passes a minted step-up authorization id (BK-8). */
  cancel: (
    venueId: string,
    bookingId: string,
    body: StaffCancelBookingRequest,
    stepUpAuthId?: string,
  ) =>
    customInstance<StaffBookingResponse>(
      `${base(venueId)}/${encodeURIComponent(bookingId)}/cancel`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(stepUpAuthId ? { [STEP_UP_HEADER]: stepUpAuthId } : {}),
        },
        body: JSON.stringify(body),
      },
    ),

  /**
   * Approve a PENDING request-to-book booking (request-to-book, R6): the server re-passes the atomic
   * claim consuming the standing hold → CONFIRMED. A lapsed hold / consumed capacity is a 409 typed
   * rejection with live alternatives; a conflicting decision is a 409 stale-write with the current record.
   */
  approveRequest: (venueId: string, bookingId: string, body: ApproveRequestBody) =>
    customInstance<StaffBookingResponse>(
      `${base(venueId)}/${encodeURIComponent(bookingId)}/request/approve`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      },
    ),

  /** Decline a PENDING request — releases the hold; optional reason + live-checked proposed alternative. */
  declineRequest: (venueId: string, bookingId: string, body: DeclineRequestBody) =>
    customInstance<StaffBookingResponse>(
      `${base(venueId)}/${encodeURIComponent(bookingId)}/request/decline`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      },
    ),
};
